/**
 * HHD ATC Modal Custom Element
 * Refactored from snippets/hhd-atc-modal.liquid
 */
if (!customElements.get('hhd-atc-modal')) {
  class HHDAtcModal extends HTMLElement {
    constructor() {
      super();
      this.currentProduct = null;
      this.selectedOptions = {};
      this.selectedVariant = null;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;

      /* Move modal to body so position:fixed works regardless of parent transforms */
      if (this.parentNode !== document.body) {
        document.body.appendChild(this);
        return;
      }

      this.init();
      this.initialized = true;
    }

    init() {
      this.overlay = this.querySelector('.hhd-modal-overlay');
      this.body = this.querySelector('[data-hhd-modal-body]');
      this.closeBtn = this.querySelector('[data-hhd-modal-close]');
      this.atcBtn = this.querySelector('[data-hhd-modal-atc]');
      this.moneyFmt = this.dataset.moneyFormat;

      if (!this.overlay || !this.body || !this.closeBtn || !this.atcBtn) return;

      this.bindEvents();
    }

    bindEvents() {
      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
      });

      this.atcBtn.addEventListener('click', () => this.addToCart());

      /* Global handler for "+ ADD" buttons */
      document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('.hhd-slider-card__add-btn');
        if (!addBtn) return;

        e.preventDefault();
        const handle = addBtn.dataset.productHandle;
        if (handle) {
          this.open(handle, addBtn.dataset.pairsCount, addBtn.dataset.perPairLabel);
        }
      });
    }

    formatMoney(cents) {
      if (!this.moneyFmt) return cents;
      let amount = (cents / 100).toFixed(2);
      amount = amount.replace(/\.00$/, '');
      return this.moneyFmt
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
    }

    findVariant() {
      if (!this.currentProduct) return null;
      const opts = this.currentProduct._optionNames || this.currentProduct.options;
      return this.currentProduct.variants.find(v => {
        return opts.every((opt, i) => {
          const optName = typeof opt === 'object' ? opt.name : opt;
          return v.options[i] === this.selectedOptions[optName];
        });
      }) || null;
    }

    open(productHandle, pairsCount = 1, perPairLabel = '') {
      this.pairsCount = parseInt(pairsCount) || 1;
      this.perPairLabel = perPairLabel || '';
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      this.body.innerHTML = '<div class="hhd-modal__loading">Loading...</div>';
      this.atcBtn.disabled = true;

      fetch(`/products/${productHandle}.js`)
        .then(r => r.json())
        .then(product => {
          this.currentProduct = product;
          this.selectedOptions = {};
          this.selectedVariant = null;

          const optionNames = product.options.map(opt => {
            return typeof opt === 'object' ? opt.name : opt;
          });
          this.currentProduct._optionNames = optionNames;

          const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
          optionNames.forEach((optName, i) => {
            this.selectedOptions[optName] = firstAvailable.options[i];
          });
          this.selectedVariant = firstAvailable;

          this.render();
        })
        .catch(e => {
          console.error('HHD Modal error:', e);
          this.body.innerHTML = '<div class="hhd-modal__loading">Failed to load product.</div>';
        });
    }

    close() {
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.currentProduct = null;
    }

    render() {
      const p = this.currentProduct;
      const v = this.selectedVariant;

      /* Image */
      let imgSrc = '';
      if (v && v.featured_image && v.featured_image.src) {
        imgSrc = v.featured_image.src.replace(/(\.[^.]+)$/, '_400x$1');
      } else if (p.featured_image) {
        imgSrc = p.featured_image.replace(/(\.[^.]+)$/, '_400x$1');
      }

      /* Price */
      let priceHtml = '';
      if (v) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          priceHtml = `<span class="hhd-modal__price-sale">${this.formatMoney(v.price)}</span>` +
            `<span class="hhd-modal__price-compare">${this.formatMoney(v.compare_at_price)}</span>`;
        } else {
          priceHtml = `<span>${this.formatMoney(v.price)}</span>`;
        }

        if (this.pairsCount > 1) {
          const pricePerPairCents = Math.round(v.price / this.pairsCount);
          priceHtml += `
            <div class="price-hhd__badge">
              <span class="price-hhd__icon-svg">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.35 6.15L6.85 0.65C6.65 0.45 6.35 0.35 6.1 0.35H1.35C0.8 0.35 0.35 0.8 0.35 1.35V6.1C0.35 6.35 0.45 6.65 0.65 6.85L6.15 12.35C6.35 12.55 6.65 12.65 6.85 12.65C7.1 12.65 7.4 12.55 7.6 12.35L12.35 7.6C12.55 7.4 12.65 7.1 12.65 6.85C12.65 6.65 12.55 6.35 12.35 6.15ZM2.85 4.35C2.025 4.35 1.35 3.675 1.35 2.85C1.35 2.025 2.025 1.35 2.85 1.35C3.675 1.35 4.35 2.025 4.35 2.85C4.35 3.675 3.675 4.35 2.85 4.35Z" fill="currentColor"/>
                </svg>
              </span>
              <span class="price-hhd__badge-text">${this.formatMoney(pricePerPairCents)} ${this.perPairLabel}</span>
            </div>
          `;
        }
      }

      let html = `
        <div class="hhd-modal__product-header">
          <div class="hhd-modal__product-image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${p.title.replace(/"/g, '&quot;')}">` : ''}
          </div>
          <div class="hhd-modal__product-info">
            <h3 class="hhd-modal__product-title">${p.title}</h3>
            <div class="hhd-modal__product-price">${priceHtml}</div>
          </div>
        </div>
      `;

      const optNames = p._optionNames || p.options.map(o => typeof o === 'object' ? o.name : o);
      const isDefaultVariant = p.variants.length === 1 && p.variants[0].title === 'Default Title';

      if (!isDefaultVariant) {
        optNames.forEach((optionName, optIndex) => {
          const values = [];
          p.variants.forEach(variant => {
            const val = variant.options[optIndex];
            if (values.indexOf(val) === -1) values.push(val);
          });

          html += `
            <div class="hhd-modal__option-group">
              <div class="hhd-modal__option-label">${optionName}: <span>${this.selectedOptions[optionName] || ''}</span></div>
              <div class="hhd-modal__option-values">
                ${values.map(val => {
            const isSelected = this.selectedOptions[optionName] === val;
            const isAvailable = p.variants.some(variant => {
              if (variant.options[optIndex] !== val) return false;
              return optNames.every((otherOpt, otherIdx) => {
                if (otherIdx === optIndex) return true;
                return !this.selectedOptions[otherOpt] || variant.options[otherIdx] === this.selectedOptions[otherOpt];
              }) && variant.available;
            });

            let classes = 'hhd-modal__option-btn';
            if (isSelected) classes += ' is-selected';
            if (!isAvailable) classes += ' is-unavailable';

            return `<button type="button" class="${classes}" data-option-name="${optionName}" data-option-value="${val.replace(/"/g, '&quot;')}" data-option-index="${optIndex}">${val}</button>`;
          }).join('')}
              </div>
            </div>
          `;
        });
      }

      html += '<div class="hhd-modal__error" data-hhd-modal-error></div>';
      this.body.innerHTML = html;

      this.updateAtcState();

      /* Bind option clicks */
      this.body.querySelectorAll('.hhd-modal__option-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const optName = e.target.dataset.optionName;
          const optVal = e.target.dataset.optionValue;

          this.selectedOptions[optName] = optVal;
          this.selectedVariant = this.findVariant();
          this.render();
        });
      });
    }

    updateAtcState() {
      if (this.selectedVariant && this.selectedVariant.available) {
        this.atcBtn.disabled = false;
        this.atcBtn.textContent = 'ADD TO CART';
      } else if (this.selectedVariant && !this.selectedVariant.available) {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'SOLD OUT';
      } else {
        this.atcBtn.disabled = true;
        this.atcBtn.textContent = 'UNAVAILABLE';
      }
    }

    addToCart() {
      if (!this.selectedVariant || !this.selectedVariant.available) return;

      this.atcBtn.classList.add('is-loading');
      this.atcBtn.textContent = 'ADDING...';

      const formData = {
        items: [{
          id: this.selectedVariant.id,
          quantity: 1
        }]
      };

      fetch((window.FoxTheme?.routes?.cart_add_url || '/cart/add') + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      })
        .then(r => r.json())
        .then(response => {
          if (response.status && response.status !== 200) {
            const errEl = this.body.querySelector('[data-hhd-modal-error]');
            if (errEl) {
              errEl.textContent = response.description || 'Could not add to cart.';
              errEl.style.display = 'block';
            }
            return;
          }

          /* Success — update cart state */
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: response }
          }));

          document.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: true }
          }));

          this.close();
        })
        .catch(e => {
          console.error('HHD ATC error:', e);
        })
        .finally(() => {
          this.atcBtn.classList.remove('is-loading');
          this.atcBtn.textContent = 'ADD TO CART';
        });
    }
  }

  customElements.define('hhd-atc-modal', HHDAtcModal);
}
